import { useCallback, useEffect, useMemo } from 'react'
import { useLatest } from '@nfinite/react-hubstairs/hooks'

import {
  useQuery as useQueryApollo, // eslint-disable-line no-restricted-imports
  useMutation as useMutationApollo, // eslint-disable-line no-restricted-imports
  useLazyQuery as useLazyQueryApollo, // eslint-disable-line no-restricted-imports
  useBackgroundQuery as useBackgroundQueryApollo, // eslint-disable-line no-restricted-imports
  useReadQuery as useReadQueryApollo, // eslint-disable-line no-restricted-imports
  useSuspenseQuery as useSuspenseQueryApollo, // eslint-disable-line no-restricted-imports
  useApolloClient,
} from '@apollo/client'

import { useOptionOverrideQuery, useOptionOverrideMutation } from './helpers/useOptionOverride'

import { hasErrorCode, ERROR_CODE } from 'requests/errors'
import { TYPES } from 'components/Error'
import { log } from 'utils/logger'

function useManualOnCompletedOnError({ data, error, onCompleted, onError, noThrow }) {
  const clearCache = useClearCache()

  const memoOnCompleted = useLatest(onCompleted)
  const memoOnError = useLatest(onError)

  const memoData = useMemo(() => data, [data])
  const memoError = useMemo(() => error, [error])

  // Trigger manually onCompleted & onError because of known issue: https://github.com/apollographql/apollo-client/issues/5708
  useEffect(() => {
    if (memoError && !memoData) {
      if (memoError?.message?.includes(TYPES.NOT_LOGGED_IN) && !noThrow?.includes(ERROR_CODE.NOT_LOGGED_IN)) {
        throw new Error(TYPES.NOT_LOGGED_IN)
      }

      if (hasErrorCode(memoError, ERROR_CODE.USER_DISABLED) && !noThrow?.includes(ERROR_CODE.USER_DISABLED)) {
        clearCache()
        throw new Error(TYPES.USER_DISABLED)
      }
      memoOnError.current && memoOnError.current(memoError)
    } else if (memoData) {
      memoOnCompleted.current && memoOnCompleted.current(memoData)
    }
  }, [clearCache, memoOnCompleted, memoOnError, memoError, memoData, noThrow])
}

export function useQuery(query, options) {
  const { noThrow, onCompleted, onError, ...queryOptions } = useOptionOverrideQuery(options)
  const { error, data, ...result } = useQueryApollo(query, queryOptions)
  useManualOnCompletedOnError({ onCompleted, onError, error, data, noThrow })

  return { error, data, ...result }
}

export function useSuspenseQuery(query, options) {
  const { onCompleted, onError, ...queryOptions } = useOptionOverrideQuery(options)
  const result = useSuspenseQueryApollo(query, queryOptions)
  return result
}

function useThrowPageError({ error, data }) {
  const clearCache = useClearCache()

  if (error && !data) {
    const { graphQLErrors = [] } = error
    if (graphQLErrors.some(e => e.message.includes(TYPES.NOT_LOGGED_IN))) {
      throw new Error(TYPES.NOT_LOGGED_IN)
    } else if (graphQLErrors.some(e => e.message.includes('Cannot return null for non-nullable'))) {
      throw new Error(TYPES.RESOURCE_NOT_FOUND)
    } else if (hasErrorCode(error, ERROR_CODE.USER_DISABLED)) {
      clearCache()
      throw new Error(TYPES.USER_DISABLED)
    } else {
      throw new Error(error)
    }
  }
}

export function usePageQuery(query, options) {
  const { noThrow, onCompleted, onError, ...queryOptions } = useOptionOverrideQuery(options)
  const { error, data, ...result } = useQueryApollo(query, queryOptions)
  useManualOnCompletedOnError({ onCompleted, onError, error, data, noThrow })

  useThrowPageError({ error, data })

  return { error, data, ...result }
}

export function useBackgroundPageQuery(query, options) {
  const optionsOverride = useOptionOverrideQuery(options)
  const [queryRef] = useBackgroundQueryApollo(query, optionsOverride)

  return [queryRef]
}

export function useReadPageQuery(queryRef) {
  const { data, error } = useReadQueryApollo(queryRef)
  useThrowPageError({ error, data })
  return { data, error }
}

export function useMutation(mutation, options) {
  const { onCompleted, onError, ...mutationOptions } = useOptionOverrideMutation(options)
  const [mutate, result] = useMutationApollo(mutation, mutationOptions)
  const { data, error } = result

  useManualOnCompletedOnError({ onCompleted, onError, error, data })

  const mutateOverride = useCallback(
    async (...args) => {
      try {
        const { errors, ...rest } = await mutate(...args)
        return { error: errors, ...rest }
      } catch (error) {
        // Calling directly the microservice (eg: @client mutations) can throw
        return { error }
      }
    },
    [mutate],
  )

  return [mutateOverride, result]
}

export function useLazyQuery(query, options) {
  const optionsOverride = useOptionOverrideQuery(options)
  const [executeQuery, { error, data, ...result }] = useLazyQueryApollo(query, optionsOverride)
  const clearCache = useClearCache()

  if (error && !data) {
    if (hasErrorCode(error, ERROR_CODE.USER_DISABLED)) {
      clearCache()
      throw new Error(TYPES.USER_DISABLED)
    }
  }
  return [executeQuery, { error, data, ...result }]
}

/**
 * clearCache will clean the apollo client store, a callback can be passed and will be called when the store is cleaned
 */
export function useClearCache() {
  const client = useApolloClient()

  const clearCache = useCallback(
    cb => {
      client.stop()
      cb && client.onClearStore(cb)
      client.clearStore()
    },
    [client],
  )

  return clearCache
}

/**
 * A helper to generate the necessary arguments to refetch queries after a mutation
 *
 * Inspired from https://www.apollographql.com/docs/react/data/mutations/#refetching-after-update
 *
 * The strategy implemented here is that by passing query names as strings (rather than gql signatures),
 * all ACTIVE queries will trigger onQueryUpdated, where we get the observableQuery, and can then do checks
 * on wether or not that query should be refetched
 *
 * ⚠️ This strategy heavily relies on having unique query names accross the application
 *
 * PARAMS:
 *
 * refetchQueries: an array of query names that will trigger onQueryUpdated if active (ie, the query is happening somewhere on the page)
 *
 * shouldRefetch (optional): for each active query in refetchQueries, shouldRefetch will be called
 *    It exposes the object observableQuery: {
 *        queryName: the name of the query
 *        last: the last variables passed to the query
 *        ...rest
 *    }
 */
export function generateRefetchQueries({ refetchQueries, shouldRefetch, awaitRefetchQueries = true }) {
  return {
    refetchQueries,
    onQueryUpdated: async observableQuery => {
      const { queryName } = observableQuery
      try {
        const isIncluded = refetchQueries.includes(queryName)
        const hasRefetch = !shouldRefetch || shouldRefetch(observableQuery)
        if (isIncluded && hasRefetch) {
          return observableQuery.refetch()
        }
      } catch (error) {
        // silently catch error
        log.warning(`An error occurred while attempting to refetch ${queryName}: ${error}`)
      }
    },
    awaitRefetchQueries,
  }
}

export const TODO_mutationOnCompleted = null
export const TODO_mutationOnError = null
export const TODO_mutationParams = null
